import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Award, PlayCircle, ThumbsUp, ThumbsDown, Star, User2, ChevronRight, X, Loader2, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";

const VERDICTS = [
  { id: "approve", label: "Approve", color: "emerald", Icon: ThumbsUp },
  { id: "reject", label: "Reject", color: "red", Icon: ThumbsDown },
  { id: "recommend_tier", label: "Recommend for Tier Laurel", color: "amber", Icon: Sparkles },
];

export default function JuryDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [queue, setQueue] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("queue");
  const [modalFilm, setModalFilm] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/jury/me").then((r) => setMe(r.data)),
      api.get("/jury/dashboard/queue").then((r) => setQueue(r.data.queue || [])),
      api.get("/jury/dashboard/completed").then((r) => setCompleted(r.data.completed || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user?.user_id) nav("/login");
    else load();
  }, [user, nav]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!me?.jury && !me?.is_admin) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-24 px-6 max-w-2xl mx-auto text-center">
          <User2 className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Jury access only</h1>
          <p className="text-gray-400">This dashboard is for World Film Festival jury members. Ask an admin to register you as jury.</p>
        </div>
      </div>
    );
  }

  const jury = me.jury;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-24 px-6 lg:px-12 max-w-6xl mx-auto pb-20">
        <header className="mb-8 flex flex-col md:flex-row md:items-center gap-5">
          {jury?.photo_url ? (
            <img src={jury.photo_url.startsWith("http") ? jury.photo_url : `${process.env.REACT_APP_BACKEND_URL}${jury.photo_url}`} alt={jury?.display_name} className="w-20 h-20 rounded-full object-cover border-2 border-amber-300/50" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-[#1a0608] border-2 border-amber-300/50 flex items-center justify-center text-3xl font-black text-[#E50914]">
              {(jury?.display_name || user?.name || "J").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-300 mb-1">World Film Festival · Jury</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">{jury?.display_name || user?.name}</h1>
            {jury?.slug && (
              <Link data-testid="jury-public-profile-link" to={`/jury/${jury.slug}`} className="text-sm text-gray-400 hover:text-amber-300 transition inline-flex items-center gap-1 mt-1">
                Public profile · /jury/{jury.slug} <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 text-center">
            <Stat n={queue.length} label="Pending" />
            <Stat n={completed.length} label="Voted" />
          </div>
        </header>

        <div className="flex gap-1 border-b border-white/10 mb-6">
          <TabBtn active={tab === "queue"} onClick={() => setTab("queue")} testid="jury-tab-queue">Pending queue ({queue.length})</TabBtn>
          <TabBtn active={tab === "completed"} onClick={() => setTab("completed")} testid="jury-tab-completed">My votes ({completed.length})</TabBtn>
        </div>

        {tab === "queue" ? (
          queue.length === 0 ? <Empty label="You're all caught up. New submissions appear here as they arrive." /> :
          <div className="grid gap-3">
            {queue.map((f) => (
              <FilmRow key={f.submission_id} film={f} onWatch={() => setModalFilm({ ...f, mode: "vote" })} />
            ))}
          </div>
        ) : (
          completed.length === 0 ? <Empty label="No votes yet. Start with the Pending queue." /> :
          <div className="grid gap-3">
            {completed.map((f) => (
              <FilmRow key={f.submission_id} film={f} vote={f.my_vote} onWatch={() => setModalFilm({ ...f, mode: "review", vote: f.my_vote })} />
            ))}
          </div>
        )}

        {modalFilm && (
          <VoteModal
            film={modalFilm}
            existingVote={modalFilm.vote}
            onClose={() => setModalFilm(null)}
            onVoted={() => { setModalFilm(null); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="text-2xl font-black tabular-nums">{n}</div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${active ? "border-amber-400 text-white" : "border-transparent text-gray-400 hover:text-white"}`}>
      {children}
    </button>
  );
}

function Empty({ label }) {
  return (
    <div className="py-16 text-center bg-white/[0.02] rounded-lg border border-white/5">
      <Award className="w-10 h-10 mx-auto text-gray-600 mb-3" />
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}

function FilmRow({ film, vote, onWatch }) {
  const poster = film.poster_url ? (film.poster_url.startsWith("http") ? film.poster_url : `${process.env.REACT_APP_BACKEND_URL}${film.poster_url}`) : null;
  const verdictColour = {
    approve: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    reject: "bg-red-500/15 text-red-300 border-red-500/30",
    recommend_tier: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  }[vote?.verdict];
  return (
    <div data-testid={`jury-film-${film.submission_id}`} className="flex gap-4 p-4 bg-[#141414] border border-white/5 rounded-lg hover:border-amber-300/30 transition-colors">
      {poster ? (
        <img src={poster} alt={film.title} className="w-16 h-24 object-cover rounded border border-white/10 shrink-0" />
      ) : (
        <div className="w-16 h-24 rounded bg-[#1a0608] border border-white/10 flex items-center justify-center shrink-0"><Award className="w-6 h-6 text-amber-300/40" /></div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold truncate">{film.title || "Untitled"}</h3>
        <div className="text-xs text-gray-500 mt-0.5">{[film.genre, film.language, film.duration_min ? `${film.duration_min}′` : null].filter(Boolean).join(" · ")}</div>
        {film.synopsis && <p className="text-xs text-gray-400 mt-1.5 line-clamp-2">{film.synopsis}</p>}
        {vote && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border ${verdictColour}`}>{vote.verdict.replace("_", " ")}</span>
            <span className="inline-flex items-center gap-1 text-[10px] text-amber-300 font-semibold"><Star className="w-3 h-3 fill-current" /> {vote.score}/10</span>
            {vote.comment && <span className="text-[10px] text-gray-500 truncate">— {vote.comment}</span>}
          </div>
        )}
      </div>
      <button data-testid={`jury-watch-${film.submission_id}`} onClick={onWatch} className="btn-red px-4 py-2 rounded font-semibold text-sm inline-flex items-center gap-1.5 self-start">
        <PlayCircle className="w-4 h-4" /> {vote ? "Review" : "Watch & vote"}
      </button>
    </div>
  );
}

function VoteModal({ film, existingVote, onClose, onVoted }) {
  const [score, setScore] = useState(existingVote?.score || 7);
  const [verdict, setVerdict] = useState(existingVote?.verdict || "approve");
  const [comment, setComment] = useState(existingVote?.comment || "");
  const [saving, setSaving] = useState(false);

  const filmSrc = film.film_path
    ? (film.film_path.startsWith("http") ? film.film_path : `${process.env.REACT_APP_BACKEND_URL}/api/files/${film.film_path.replace(/^\/api\/files\//, "")}`)
    : null;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/jury/vote/${film.submission_id}`, { verdict, score, comment });
      if (data.consensus_verdict) {
        toast.success(`Consensus reached — status → ${data.new_status}`);
      } else {
        toast.success(`Vote recorded · ${verdict.replace("_", " ")} · ${score}/10`);
      }
      onVoted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div data-testid="jury-vote-modal" className="bg-[#141414] border border-white/10 rounded-2xl w-full max-w-4xl my-8">
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">Jury Review · {film.filmmaker_name || "Filmmaker"}</div>
            <h2 className="text-xl font-bold">{film.title}</h2>
          </div>
          <button data-testid="jury-vote-close" onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {filmSrc ? (
          <video src={filmSrc} controls className="w-full max-h-[55vh] bg-black" />
        ) : (
          <div className="bg-black py-16 text-center text-gray-500 text-sm">Film file not available — vote on synopsis + press materials.</div>
        )}

        <div className="p-6 space-y-5">
          {film.synopsis && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Synopsis</div>
              <p className="text-sm text-gray-300 leading-relaxed">{film.synopsis}</p>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 block">Score · <span className="text-amber-300 font-bold text-lg">{score}/10</span></label>
            <input data-testid="jury-vote-score" type="range" min="1" max="10" step="1" value={score} onChange={(e) => setScore(parseInt(e.target.value))} className="w-full accent-amber-400" />
            <div className="flex justify-between text-[10px] text-gray-600 mt-1"><span>1 · Poor</span><span>5 · Average</span><span>10 · Masterpiece</span></div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 block">Verdict</label>
            <div className="grid grid-cols-3 gap-3">
              {VERDICTS.map((v) => {
                const active = verdict === v.id;
                const styles = { emerald: "border-emerald-500/60 bg-emerald-500/10 text-emerald-300", red: "border-red-500/60 bg-red-500/10 text-red-300", amber: "border-amber-500/60 bg-amber-500/10 text-amber-300" }[v.color];
                return (
                  <button key={v.id} data-testid={`jury-verdict-${v.id}`} onClick={() => setVerdict(v.id)} type="button"
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1.5 transition-all ${active ? styles : "border-white/10 bg-white/[0.02] text-gray-400 hover:border-white/25"}`}>
                    <v.Icon className="w-5 h-5" />
                    <span className="text-xs font-semibold">{v.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Comment (optional)</label>
            <textarea
              data-testid="jury-vote-comment"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What worked, what didn't. Only other jury and admins see this."
              className="input-dark w-full text-sm"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button data-testid="jury-vote-submit" onClick={submit} disabled={saving} className="btn-red flex-1 py-3 rounded font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (existingVote ? "Update vote" : "Cast vote")}
            </button>
            <button type="button" onClick={onClose} className="btn-glass px-6 py-3 rounded font-semibold">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
